import './S_ListSelect.scss';
import { ReactElement, ReactNode, useState } from 'react';
import { v4 } from 'uuid';
import { S_Checkbox } from '../checkbox/S_Checkbox';
import { S_TextInput } from '../textInput/S_TextInput';

interface IOption<T> {
  value: T;
  label: ReactNode;
  searchableLabel: string;
}

interface IOptionProps<T> {
  option: IOption<T>;
  isSelected: boolean;
  onSelect: (value: T) => void;
}

interface IProps<T> {
  options: IOption<T>[];
  selected: T[];
  onChange: (values: T[]) => void;
}

const S_ListSelectOption = <T,>({ option, isSelected, onSelect }: IOptionProps<T>): ReactElement => {
  const [id] = useState(v4());
  return (
    <div className='s-list-select-option'>
      <S_Checkbox
        id={id}
        checked={isSelected}
        onChange={(): void => {
          onSelect(option.value);
        }}
        label={option.label}
      />
    </div>
  );
};

export const S_ListSelect = <T,>({ options, selected, onChange }: IProps<T>): ReactElement => {
  const [filter, setFilter] = useState<string>('');

  const filteredOptions = options.filter((o) => o.searchableLabel.toLowerCase().includes(filter.toLowerCase()));
  const allSelected = options.length === selected.length;

  const handleOptionSelect = (value: T): void => {
    if (selected.some((x) => x === value)) {
      onChange(selected.filter((x) => x !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleSelectAll = (): void => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map((x) => x.value));
    }
  };

  return (
    <div className='s-list-select'>
      <S_TextInput
        className='s-list-select-search'
        onChange={setFilter}
        value={filter}
        placeholder='Search...'
      />
      <div className='s-list-select-options'>
        <S_ListSelectOption
          option={{ label: 'Select All', searchableLabel: '', value: '' }}
          isSelected={allSelected}
          onSelect={handleSelectAll}
        />
        {filteredOptions.map((o) => {
          return (
            <S_ListSelectOption
              key={`${o.value}`}
              option={o}
              isSelected={selected.some((s) => s === o.value)}
              onSelect={handleOptionSelect}
            />
          );
        })}
      </div>
    </div>
  );
};
